import {
  goToRecipients,
  saveFilter,
  simulateSubs,
} from './../services/services'
import { type Getter, type Setter } from 'jotai'
import {
  defaultSubscriberState,
  filterFormAtom,
  goToRecipientModalAtom,
  originalFilterAtom,
  subscriberCountAtom,
} from '../state/state'
import { logAppError } from '../../../helpers/logger'
import { notifyError, notifySuccess } from '../../../helpers/notifyToast'
import { dirtyFieldsAtom } from '../state/validation'
import {
  errorUpdater,
  loaderUpdater,
  pageState,
  statusUpdater,
} from '../../../state/page'
import { type UiFilterDetail } from '../filterConditionTypes'
import { type ChannelType } from '../../../types/FilterCondition'
import { openCategoryFormCallback } from '../../newCategoriesListPage/callbacks/form'
import { type Category } from '../../../types/beFiltersApi'

export const modifyFilterCallback = (
  _: Getter,
  set: Setter,
  part: Partial<UiFilterDetail>,
): void => {
  set(filterFormAtom, (prev) => {
    return {
      ...prev,
      ...part,
    }
  })
  set(dirtyFieldsAtom, (prev) => {
    return [...new Set([...prev, ...Object.keys(part)])]
  })
}

export const saveFilterCallback = async (
  get: Getter,
  set: Setter,
): Promise<void> => {
  const filter = get(filterFormAtom)
  const isNew = filter.Id === 0
  set(pageState, loaderUpdater(true))
  try {
    const savedFilter = await saveFilter(filter)
    set(filterFormAtom, savedFilter)
    set(originalFilterAtom, savedFilter)

    set(pageState, statusUpdater('ready'))
    const msg = isNew
      ? 'Load.CombinedFilterForm.FilterCreated'
      : 'Load.Alerts.SuccessUpdate'
    notifySuccess(msg)
  } catch (e) {
    console.error('### error', e)
    logAppError({
      Component: 'CombinedFilterFormPage',
      Method: 'saveFilterCallback',
      appError: e instanceof Error ? e : undefined,
    })
    set(pageState, errorUpdater)
  }

  set(pageState, loaderUpdater(false))
}

export const simulateSubsCallback = async (
  get: Getter,
  set: Setter,
): Promise<void> => {
  set(subscriberCountAtom, { ...defaultSubscriberState, loading: true })
  try {
    const filter = get(filterFormAtom)
    const count = await simulateSubs(filter)
    set(subscriberCountAtom, { count, loading: false })
  } catch (error: any) {
    logAppError({
      Component: 'CombinedFilterFormPage',
      Method: 'simulateSubsCallback',
      appError: error,
    })
    notifyError('Load.Common.GenericErrorApplyFilters')
    set(subscriberCountAtom, defaultSubscriberState)
  }
}

export const tryGoToRecipientsOrOpenModalCallback = (
  get: Getter,
  set: Setter,
  channel: ChannelType,
): void => {
  const filter = get(filterFormAtom)
  if (
    filter.Id !== 0 &&
    JSON.stringify(filter) === JSON.stringify(get(originalFilterAtom))
  ) {
    goToRecipients(channel, filter.Id)
  } else {
    set(goToRecipientModalAtom, { show: true, type: channel })
  }
}

export const saveAndGoToRecipientsCallback = async (
  get: Getter,
  set: Setter,
): Promise<void> => {
  await saveFilterCallback(get, set)
  goToRecipientsCallback(get)
}

export const goToRecipientsCallback = (get: Getter): void => {
  const { type } = get(goToRecipientModalAtom)
  const { Id } = get(filterFormAtom)
  if (type !== undefined) {
    goToRecipients(type, Id)
  }
}

export const openCreateCategoryFormCallback = (
  get: Getter,
  set: Setter,
): void => {
  openCategoryFormCallback(get, set, undefined, setCategoryCallback)
}

export const setCategoryCallback = (
  get: Getter,
  set: Setter,
  category: Category,
): void => {
  modifyFilterCallback(get, set, {
    Category: category.Id,
  })
}
